// Per-user permission helpers — kept in one tiny module so feature
// toggling / removal is a single file change.
//
// `refreshCurrentUserMe()` is called after any mutation that *could* change
// the active user's role/permissions (admin edits another user, the logged-
// in admin updates their own role, etc.). It re-hits /guardian-users/me,
// persists the new payload to userStorage, and dispatches the
// permissions-updated event so the sidebar and any <PermissionGuard>
// re-render without a full page reload.
//
// Focus-driven refreshes are throttled via `FOCUS_REFRESH_TTL_MS` so an
// alt-tab doesn't fire a `/me` round-trip on every focus tick. Mutation
// callers should pass `{ force: true }` to bypass the throttle.

import { getGuardianPermissionsApi } from '../api/permissionApi'
import { notifyPermissionsChanged } from '../hooks/usePermissionEvents'
import { getGuardianMeApi } from '../pages/auth/productSelection/api'

import { permissionStorage } from './permissionStorage'
import { userStorage } from './userStorage'

// Module-level dedup state. Sharing this between `refreshCurrentUserMe`
// and the focus handler in `useEffectivePermissions` guarantees that the
// hook's own ref-based guard and explicit callers can't fire overlapping
// network requests at the same instant.
let refreshInFlight = false
let lastMeFetchAt = 0

// Minimum gap between focus-driven /me refreshes. Long enough that
// rapid alt-tab patterns don't hammer the endpoint, short enough that
// a role change made by an admin lands within a tolerable window.
export const FOCUS_REFRESH_TTL_MS = 5 * 60 * 1000

// Exposed for diagnostics / tests so we can verify the throttle is
// actually firing.
export const getLastMeFetchAt = () => lastMeFetchAt

/**
 * Fetch the current /me payload, persist it, and notify all subscribers
 * (sidebar + route guards) that permissions may have changed.
 *
 * Best-effort: silently swallows network/parse errors so callers don't
 * have to wrap the call site in try/catch. The previously cached payload
 * remains in userStorage, so the sidebar falls back to "last known
 * permissions" rather than locking the user out.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.force=false] Skip the TTL throttle. Use after
 *   a mutation that explicitly needs fresh permission data.
 */
export async function refreshCurrentUserMe({ force = false } = {}) {
  if (!force && Date.now() - lastMeFetchAt < FOCUS_REFRESH_TTL_MS) return
  if (refreshInFlight) return
  refreshInFlight = true
  try {
    const raw = await getGuardianMeApi()
    const payload = raw?.data && typeof raw.data === 'object' ? raw.data : null
    if (payload) {
      userStorage.setUserDetails(payload)
      lastMeFetchAt = Date.now()
    }
    notifyPermissionsChanged()
  } catch {
    // Non-fatal — the cached snapshot stays put. The next /me fetch on
    // tab focus, login, or another mutation will reconcile.
  } finally {
    refreshInFlight = false
  }
}

/**
 * Synchronously read the current user's role UUID from the cached /me
 * payload. Tries the common shapes the backend has used across versions
 * (`roleUuid`, `role.uuid`, `role`) and returns `null` when nothing
 * matches. Used by role/user-management flows to decide whether an
 * external edit affected the current user.
 */
export function getCurrentUserRoleUuid(userDetails) {
  const details = userDetails && typeof userDetails === 'object' ? userDetails : null
  if (!details) return null
  const candidates = [
    details.roleUuid,
    details.roleUUID,
    details.roleUuidLower,
    details.role?.uuid,
    details.Role?.uuid,
  ]
  for (const c of candidates) {
    if (c != null && c !== '') return String(c)
  }
  // Fallback: string-shaped `role` (older payloads treated role as a UUID).
  if (typeof details.role === 'string' && details.role) return details.role
  return null
}

/**
 * Fetch the branch-scoped permission payload (`GET /guardian-users/permission`)
 * for `branch` and cache it in localStorage.
 *
 * Called from the Branch Selection screen and the sidebar BranchSwitcher
 * right before the picked branch becomes the selected one, so the request
 * carries the *new* branch's `branch-id` header.
 *
 * Best-effort by design: the function never throws. Callers that must not
 * proceed without permissions (both branch-switch entry points) check
 * `result.ok` and abort the switch when it is `false`.
 *
 * @param {object} branch Branch row — the numeric `id` is preferred, the
 *   `uuid` is used as a fallback.
 * @returns {Promise<{ ok: boolean, record?: object }>} `ok: true` when the
 *   payload was fetched and cached.
 */
export async function fetchAndCacheBranchPermissions(branch) {
  const branchId = branch?.id ?? branch?.uuid ?? null
  const payload = await getGuardianPermissionsApi({
    branchId,
    branchUuid: branch?.uuid,
  })
  if (!payload) return { ok: false }
  const record = permissionStorage.setPermissions(payload, branchId)
  // The cached payload is the app's permission source of truth, so tell
  // every consumer (sidebar, route guard, gated buttons) to re-read it.
  notifyPermissionsChanged()
  return { ok: true, record }
}
